import React, { useState } from "react"
import { Badge, Box, Button, Heading, Input, Text } from "@chakra-ui/react"
import { RegistryDoctor, getInitials, getPhone, getRegNumber } from "./shared.types"

interface Props {
  doctors: RegistryDoctor[]
  loadingDoctors: boolean
  onRefresh: () => void
  onDocuments: (doc: RegistryDoctor) => void
  onVerification: (doc: RegistryDoctor) => void
  onCreateProfile: () => void
}

const STATUS_BADGE: Record<string, { bg: string; color: string; label: string }> = {
  active:   { bg: "#E6F4EA", color: "#137333", label: "Active" },
  pending:  { bg: "#FFF8E1", color: "#F57F17", label: "Pending Review" },
  inactive: { bg: "#FCE8E6", color: "#C5221F", label: "Inactive" },
}

const PER_PAGE = 10

export const Registry: React.FC<Props> = ({ doctors, loadingDoctors: loading, onRefresh, onDocuments, onVerification, onCreateProfile }) => {
  const [filter, setFilter] = useState<"all" | "active" | "inactive">("all")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)

  const filtered = doctors.filter((d) => {
    const matchStatus = filter === "all" || d.status === filter
    const matchSearch = !search ||
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.email.toLowerCase().includes(search.toLowerCase()) ||
      d.specialization.toLowerCase().includes(search.toLowerCase())
    return matchStatus && matchSearch
  })

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE))
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE)
  const counts = {
    all:      doctors.length,
    active:   doctors.filter((d) => d.status === "active").length,
    inactive: doctors.filter((d) => d.status === "inactive").length,
  }

  const FILTERS: { key: "all" | "active" | "inactive"; label: string }[] = [
    { key: "all",      label: `All Doctors (${counts.all})` },
    { key: "active",   label: `Active (${counts.active})` },
    { key: "inactive", label: `Inactive (${counts.inactive})` },
  ]

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={6}>
        <Box>
          <Heading size="lg" mb={1}>Doctor Registry</Heading>
          <Text fontSize="sm" color="gray.500">Manage and verify registered medical professionals.</Text>
        </Box>
        <Box display="flex" gap={2}>
          <Button size="sm" variant="outline" colorScheme="blue" onClick={onRefresh} isLoading={loading}>Refresh</Button>
          <Button size="sm" colorScheme="green" onClick={onCreateProfile}>+ Create Profile</Button>
        </Box>
      </Box>

      <Box bg="white" border="1px solid" borderColor="gray.200" borderTopRadius="lg"
        px={4} py={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box display="flex" gap={2} flexWrap="wrap">
          {FILTERS.map((f) => (
            <Box key={f.key} as="button"
              px={3} py={1} borderRadius="full" border="1px solid" fontSize="xs" fontWeight="semibold" cursor="pointer"
              bg={filter === f.key ? "blue.50" : "white"}
              borderColor={filter === f.key ? "blue.300" : "gray.200"}
              color={filter === f.key ? "blue.700" : "gray.500"}
              onClick={() => { setFilter(f.key); setPage(1) }}>
              {f.label}
            </Box>
          ))}
        </Box>
        <Input size="sm" w="220px" placeholder="Search name, email, specialty..."
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} />
      </Box>

      <Box bg="white" border="1px solid" borderColor="gray.200" borderTop="none" borderBottomRadius="lg" overflowX="auto" shadow="sm">
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
          <thead>
            <tr style={{ background: "#fafafa", borderBottom: "1px solid #e2e8f0" }}>
              {["Doctor Info", "Specialization", "Reg. Number", "Contact", "Type", "Verified", "Status", "Actions"].map((h) => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left" }}>
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold">{h}</Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: "40px", textAlign: "center" }}>
                <Text fontSize="sm" color="gray.400">Loading...</Text>
              </td></tr>
            ) : paged.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: "40px", textAlign: "center" }}>
                <Text fontSize="sm" color="gray.400">No doctors found.</Text>
              </td></tr>
            ) : paged.map((doc) => {
              const badge = STATUS_BADGE[doc.status] ?? STATUS_BADGE["inactive"]
              return (
                <tr key={doc._id} style={{ borderBottom: "1px solid #edf2f7" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#F0F5FF")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "white")}>
                  <td style={{ padding: "10px 14px" }}>
                    <Box display="flex" alignItems="center" gap={3}>
                      <Box w={8} h={8} borderRadius="full" bg="blue.100" flexShrink={0}
                        display="flex" alignItems="center" justifyContent="center" fontSize="xs" fontWeight="bold" color="blue.700">
                        {getInitials(doc.name)}
                      </Box>
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm">{doc.name}</Text>
                        <Text fontSize="xs" color="gray.400">{doc.email}</Text>
                      </Box>
                    </Box>
                  </td>
                  <td style={{ padding: "10px 14px" }}><Text fontSize="sm" color="gray.600">{doc.specialization || "—"}</Text></td>
                  <td style={{ padding: "10px 14px" }}><Text fontSize="sm" fontFamily="monospace" color="gray.600">{getRegNumber(doc)}</Text></td>
                  <td style={{ padding: "10px 14px" }}><Text fontSize="sm" color="gray.600">{getPhone(doc)}</Text></td>
                  <td style={{ padding: "10px 14px" }}>
                    {doc.doctor_type ? <Badge colorScheme="purple" fontSize="xs">{doc.doctor_type}</Badge> : <Text fontSize="xs" color="gray.400">—</Text>}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <Badge colorScheme={doc.is_verified ? "green" : "red"} fontSize="xs">
                      {doc.is_verified ? "✓ Verified" : "Unverified"}
                    </Badge>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <Box as="span" display="inline-flex" alignItems="center" px={2} py={0.5} borderRadius="sm" fontWeight={600} bg={badge.bg} color={badge.color}>
                      <Text fontSize="xs">{badge.label}</Text>
                    </Box>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <Box display="flex" gap={1}>
                      <Button size="xs" variant="outline" colorScheme="blue" onClick={() => onDocuments(doc)}>📄 Docs</Button>
                      <Button size="xs" variant="outline" colorScheme="green" onClick={() => onVerification(doc)}>✅ Verify</Button>
                    </Box>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <Box px={4} py={3} borderTop="1px solid" borderColor="gray.100" display="flex" justifyContent="space-between" alignItems="center">
          <Text fontSize="xs" color="gray.500">
            Showing <b>{filtered.length === 0 ? 0 : (page - 1) * PER_PAGE + 1}</b> to <b>{Math.min(page * PER_PAGE, filtered.length)}</b> of <b>{filtered.length}</b> results
          </Text>
          <Box display="flex" gap={1}>
            <Button size="xs" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page === 1}>‹</Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <Button key={p} size="xs" variant={p === page ? "solid" : "outline"} colorScheme={p === page ? "blue" : "gray"} onClick={() => setPage(p)}>{p}</Button>
            ))}
            <Button size="xs" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} isDisabled={page === totalPages}>›</Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
